import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { ArrowRight, UserPlus, KeyRound } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { trackEvent, EventCategory } from '../../lib/analytics'
import { SurveyCodeInput } from './survey-code-input'
import { useAuth } from '../../hooks/use-auth'

export function SurveyOptions() {
  const [showCodeInput, setShowCodeInput] = useState(false)
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const handleOptionClick = (option: string) => {
    trackEvent({
      action: 'click',
      category: EventCategory.Survey,
      label: `survey_option_${option}`
    })

    if (option === 'manage' && isAuthenticated) {
      navigate('/survey/manage')
    }
  }

  return (
    <div className="mt-16 grid gap-8 md:grid-cols-2">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card className="h-full">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-primary-foreground" />
              </div>
              <CardTitle>Create & Manage</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col h-[calc(100%-88px)]">
            <p className="text-muted-foreground flex-1">
              {isAuthenticated 
                ? "View and manage your existing surveys, or create new ones for your organisation."
                : "Sign in or create an account to create and manage surveys for your organisation. Track responses and view insights."}
            </p>
            <Button 
              onClick={() => handleOptionClick('manage')}
              className="w-full mt-4"
              asChild={!isAuthenticated}
            >
              {isAuthenticated ? (
                <span className="flex items-center">
                  Manage Surveys <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              ) : (
                <Link to="/survey/create">
                  Sign In <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              )}
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="h-full">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-primary-foreground" />
              </div>
              <CardTitle>Enter Survey Code</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col h-[calc(100%-88px)]">
            <p className="text-muted-foreground flex-1">
              Have a survey code? Enter it here to participate in your organisation's assessment.
            </p>
            {showCodeInput ? (
              <SurveyCodeInput onCancel={() => setShowCodeInput(false)} />
            ) : (
              <Button 
                className="w-full mt-4"
                onClick={() => {
                  setShowCodeInput(true)
                  handleOptionClick('enter_code')
                }}
              >
                Enter Code <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}